<template>
  <div class="settings-wrapper">
    <div class="settings">
      <h1 class="settings__title">Ustawienia</h1>
      <div class="settings__grid">
        <div class="settings__card">
          <h2 class="settings__card-title">Konto</h2>
          <div class="settings__card-content">
            <div class="settings__row">
              <label class="settings__label">Zmień hasło</label>
              <ButtonWithSpinner
                :is-loading="isLoading"
                text="Zmień"
                variant="primary"
                class="settings__button"
                @click="handlePasswordChange"
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
  <Toast />
</template>

<script setup lang="ts">
import { useToast } from 'primevue/usetoast';
import { UserService } from '@/api/user/userService';
import { useUserStore } from '@/stores/userStore/userStore';
import ButtonWithSpinner from '@/components/UI/ButtonWithSpinner.vue';
import { ref } from 'vue';

const toast = useToast();
const userStore = useUserStore();

const isLoading = ref(false);

const handlePasswordChange = async () => {
  const userLogin = userStore.getLoggedInUserLogin;

  if (!userLogin) {
    toast.add({
      severity: 'error',
      summary: 'Błąd',
      detail: 'Nie można znaleźć adresu email użytkownika',
      life: 3000,
    });
    return;
  }

  try {
    isLoading.value = true;
    await UserService.requestPasswordReset(userLogin);
    toast.add({
      severity: 'success',
      summary: 'Sukces',
      detail: 'Link do resetowania hasła został wysłany na Twój adres email',
      life: 5000,
    });
  } catch (error) {
    toast.add({
      severity: 'error',
      summary: 'Błąd',
      detail: error instanceof Error ? error.message : 'Wystąpił błąd podczas wysyłania prośby o reset hasła',
      life: 5000,
    });
  } finally {
    isLoading.value = false;
  }
};
</script>

<style lang="scss" scoped>
.settings-wrapper {
  min-height: 100vh;
  background-color: $white-100;
  padding: 2rem;
}

.settings {
  max-width: 1270px;
  margin: 0 auto;
  font-family: Mate, serif;

  &__title {
    font-size: clamp(3.6rem, 4vw, 4.8rem);
    font-weight: 700;
    margin-bottom: 1.5rem;
    color: $black-300;
  }

  &__grid {
    display: grid;
    gap: 1.5rem;
  }

  &__card {
    background-color: $white;
    border-radius: 12px;
    box-shadow: 0 8px 24px rgba($black, 0.1);
    padding: 2rem;
    border: 1px solid $brown-300;
    transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);

    &:hover {
      transform: translateY(-3px);
      box-shadow: 0 12px 28px rgba($black, 0.15);
    }

    &-title {
      font-size: clamp(2rem, 3vw, 3.2rem);
      font-weight: 800;
      margin-bottom: 1.5rem;
      color: $brown-200;
      line-height: 1;
    }

    &-content {
      display: flex;
      flex-direction: column;
      gap: 1.5rem;
    }
  }

  &__row {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 1rem;
  }

  &__label {
    color: $black-400;
    font-size: 1.7rem;
  }

  &__button {
    min-width: 120px;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 0.5rem;
    font-size: 1.5rem;
    padding: 0.75rem 1.5rem;
    border-radius: 0.5rem;
    transition: all 0.2s ease-in-out;
    max-width: 250px;
    background-color: $brown-200;
    color: $white;
    border: none;
    box-shadow: 0 4px 12px rgba($black, 0.1);
    cursor: pointer;

    &:hover {
      transform: translateY(-2px);
      box-shadow: 0 4px 12px rgba($black, 0.3);
    }

    i {
      font-size: 1.4rem;
    }
  }
}

@media (max-width: $media-max-tablet) {
  .settings-wrapper {
    padding: 1rem;
  }

  .settings {
    &__title {
      font-size: clamp(2.8rem, 3vw, 3.6rem);
      margin-bottom: 2rem;
    }

    &__card {
      padding: 1.5rem;

      &-content {
        gap: 2rem;
      }
    }

    &__row {
      align-items: flex-start;
      flex-wrap: wrap;
      gap: 1rem 10rem;
    }

    &__button {
      width: 100%;
      max-width: none;
    }
  }
}
</style>
